import { useEffect, useState, type FormEvent } from 'react';
import { Sparkles, X } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * Suggestion pills shown under the input. Mirror the examples in
 * docs/LIBRARY.md — short natural-language phrases that illustrate the
 * semantic-search sweet spot.
 *
 * Exported so the Library page (or future tests) can introspect the list
 * without reaching into the component.
 */
export const AI_SEARCH_SUGGESTIONS = [
  'Adventure books',
  'All about animals',
  'Historical figures',
  'Revolutionary War'
] as const;

export interface AiSearchHeroProps {
  /** Called when the parent submits a query — either via form or a suggestion pill. */
  onSearch: (query: string) => void;
  /** Called when the parent clears the hero (× button). */
  onClear?: () => void;
  /** Optional first-name to greet the reader with. */
  readerName?: string | null;
  /** True while a search request is in flight; disables submit + swaps button label. */
  loading?: boolean;
  /** Echoed back in the input after a programmatic search (e.g. suggestion pill). */
  activeQuery?: string | null;
  className?: string;
}

/**
 * AiSearchHero — rounded gradient hero that owns the semantic search
 * input, the "Try:" suggestion pills, and the clear-results affordance.
 *
 * Input state is kept local so the parent only hears about actual
 * submissions. `activeQuery` comes back from the parent as the canonical
 * "what's being displayed right now" so clearing the input and running
 * a suggestion both stay in sync.
 */
export function AiSearchHero({
  onSearch,
  onClear,
  readerName,
  loading = false,
  activeQuery,
  className,
}: AiSearchHeroProps) {
  const [input, setInput] = useState(activeQuery ?? '');

  // Re-sync the local input when the parent swaps `activeQuery` — e.g. a
  // suggestion pill click or a restore-from-URL. We only mirror non-null
  // values so "cleared from outside" (activeQuery becomes null) doesn't
  // blank out whatever the user was typing.
  useEffect(() => {
    if (activeQuery !== undefined && activeQuery !== null) {
      setInput(activeQuery);
    }
  }, [activeQuery]);

  const submit = (query: string) => {
    const trimmed = query.trim();
    if (!trimmed || loading) return;
    onSearch(trimmed);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submit(input);
  };

  const handleClear = () => {
    setInput('');
    if (onClear) onClear();
  };

  const hasResults = activeQuery !== undefined && activeQuery !== null && activeQuery !== '';

  return (
    <section
      className={cn(
        'rounded-[28px] border border-or-orange-100 bg-gradient-to-br from-or-cream-50 via-white to-or-orange-50 p-8 shadow-or-card',
        className,
      )}
    >
      <div className="mx-auto max-w-xl text-center">
        <h1 className="mt-2 font-display text-[30px] font-bold text-or-slate-900">
          What are you curious about today
          {readerName ? (
            <>
              , <span className="text-or-orange-600">{readerName}</span>
            </>
          ) : null}
          ?
        </h1>
        <p className="mt-2 text-[14px] text-or-slate-600">
          Ask in plain English — "funny books about space" or "chapter books readers who like
          horses".
        </p>

        <form
          onSubmit={handleSubmit}
          className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-center"
        >
          <label htmlFor="ai-search" className="sr-only">
            Search books
          </label>
          <div className="relative flex-1">
            <input
              id="ai-search"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Author, Topic…"
              autoComplete="off"
              className="h-11 w-full rounded-full border border-or-slate-200 bg-white px-5 pr-10 text-[14px] text-or-slate-900 placeholder:text-or-slate-400 focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
            />
            {input.length > 0 ? (
              <button
                type="button"
                onClick={handleClear}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-or-slate-400 hover:bg-or-slate-100 hover:text-or-slate-700"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            ) : null}
          </div>
          <button
            type="submit"
            disabled={loading || input.trim().length === 0}
            className="or-gradient-btn inline-flex h-11 w-full items-center justify-center gap-2 rounded-full px-5 text-[14px] font-semibold disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          >
            <Sparkles className="h-4 w-4" aria-hidden />
            {loading ? 'Searching…' : 'Search'}
          </button>
        </form>

        <div className="mt-4 flex flex-wrap justify-center gap-2">
          {AI_SEARCH_SUGGESTIONS.map((suggestion) => {
            const isActive = activeQuery === suggestion;
            return (
              <button
                key={suggestion}
                type="button"
                onClick={() => submit(suggestion)}
                disabled={loading}
                aria-pressed={isActive}
                className={cn(
                  'rounded-full border px-3.5 py-1.5 text-[12px] font-semibold transition',
                  isActive
                    ? 'border-or-orange-300 bg-or-orange-100 text-or-orange-800'
                    : 'border-or-orange-200 bg-white/80 text-or-orange-700 hover:bg-or-orange-50',
                  'disabled:cursor-not-allowed disabled:opacity-60',
                )}
              >
                {suggestion}
              </button>
            );
          })}
          {hasResults && onClear ? (
            <button
              type="button"
              onClick={handleClear}
              className="inline-flex items-center gap-1 rounded-full border border-or-slate-200 bg-white px-3.5 py-1.5 text-[12px] font-semibold text-or-slate-700 transition hover:bg-or-slate-50"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
              Clear results
            </button>
          ) : null}
        </div>
      </div>
    </section>
  );
}
